import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ViewAsProvider } from "./context/ViewAsContext";
import { Toaster } from "./components/ui/toaster";

// Components
import Navbar from "./components/Navbar";
import DashboardLayout from "./components/DashboardLayout";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import DashboardRouter from "./pages/DashboardRouter";
import PatientDashboard from "./pages/PatientDashboard";
import Referrals from "./pages/Referrals";
import NewReferral from "./pages/NewReferral";
import ReferralDetail from "./pages/ReferralDetail";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Contact from "./pages/Contact";
import ProviderDirectory from "./pages/ProviderDirectory";
import Patients from "./pages/Patients";
import Team from "./pages/Team";
import Analytics from "./pages/Analytics";
import DealRooms from "./pages/DealRooms";
import AIChat from "./pages/AIChat";
import EnhancedCommunications from "./pages/EnhancedCommunications";
import EnhancedMarketing from "./pages/EnhancedMarketing";
import EnhancedChallenges from "./pages/EnhancedChallenges";
import ChallengeDetail from "./pages/ChallengeDetail";
import EnhancedRewards from "./pages/EnhancedRewards";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import Community from "./pages/Community";
import CommunityTopicDetail from "./pages/CommunityTopicDetail";
import Courses from "./pages/Courses";
import Subscriptions from "./pages/Subscriptions";
import Settings from "./pages/Settings";
import UserManagement from "./pages/UserManagement";
import ClaimProfile from "./pages/ClaimProfile";
import DealRoomDetail from "./pages/DealRoomDetail";
import PatientDetail from "./pages/PatientDetail";
import ProviderEdit from "./pages/ProviderEdit";
import CoursePlayer from "./pages/CoursePlayer";
import TeamMemberProfile from "./pages/TeamMemberProfile";
import PortalCustomization from "./pages/PortalCustomization";
import ProviderProfile from "./pages/ProviderProfile";
import ProviderComparison from "./pages/ProviderComparison";
import NotificationCenter from "./pages/NotificationCenter";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();

  const isPublicRoute = () => {
    const publicPaths = ['/', '/pricing', '/about', '/contact', '/login', '/register'];
    return publicPaths.includes(location.pathname);
  };

  return (
    <>
      {isPublicRoute() && <Navbar />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DashboardRouter />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/referrals"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Referrals />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/referrals/new"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <NewReferral />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/referrals/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ReferralDetail />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Placeholder routes for future features */}
        <Route
          path="/doctors"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ProviderDirectory />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/patients"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Patients />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/patients/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <PatientDetail />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctors/:id/edit"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ProviderEdit />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctors/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ProviderProfile />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="flex items-center justify-center min-h-[60vh]">
                  <div className="text-center">
                    <h1 className="text-3xl font-bold text-[#0A1628] mb-4">Document Management</h1>
                    <p className="text-gray-600">Coming soon in Phase 2</p>
                  </div>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-3xl font-bold text-[#0A1628] mb-4">Profile</h1>
                  <p className="text-gray-600">Coming soon</p>
                </div>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Settings />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Additional Dashboard Pages */}
        <Route
          path="/team"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Team />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <UserManagement />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/claim-profile"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ClaimProfile />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deal-rooms"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DealRooms />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/deal-rooms/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <DealRoomDetail />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Analytics />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/provider-comparison"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ProviderComparison />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <NotificationCenter />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AIChat />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/communications"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <EnhancedCommunications />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketing"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <EnhancedMarketing />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/challenges"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <EnhancedChallenges />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/challenges/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <ChallengeDetail />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rewards"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <EnhancedRewards />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/blog"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Blog />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/blog/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <BlogDetail />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/community"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Community />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/community/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <CommunityTopicDetail />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Courses />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:id/player"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <CoursePlayer />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Subscriptions />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/customization"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <PortalCustomization />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/team/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <TeamMemberProfile />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="flex items-center justify-center min-h-[60vh]">
                  <div className="text-center">
                    <h1 className="text-3xl font-bold text-[#0A1628] mb-4">Leaderboard</h1>
                    <p className="text-gray-600">View top performers</p>
                  </div>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/education"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="flex items-center justify-center min-h-[60vh]">
                  <div className="text-center">
                    <h1 className="text-3xl font-bold text-[#0A1628] mb-4">Education Center</h1>
                    <p className="text-gray-600">Patient education resources</p>
                  </div>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/appointments"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <div className="flex items-center justify-center min-h-[60vh]">
                  <div className="text-center">
                    <h1 className="text-3xl font-bold text-[#0A1628] mb-4">Appointments</h1>
                    <p className="text-gray-600">View your appointments</p>
                  </div>
                </div>
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* 404 Route */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-[#0A1628] mb-4">404</h1>
                <p className="text-xl text-gray-600 mb-8">Page not found</p>
                <a href="/" className="text-[#D4AF37] hover:text-[#C19B2B]">
                  Go back home
                </a>
              </div>
            </div>
          }
        />
      </Routes>
      <Toaster />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ViewAsProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ViewAsProvider>
    </AuthProvider>
  );
}

export default App;
